import React, { useEffect, useState, useCallback } from "react";
import { TrendingUp, Clock, XCircle, Filter, Loader2, Tv, Tag } from "lucide-react";
import { api } from "@/lib/api";

const statusStyle = {
  success: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", icon: TrendingUp, label: "Success" },
  pending: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: Clock, label: "Pending" },
  failed:  { bg: "bg-red-50",   text: "text-red-700",   border: "border-red-200", icon: XCircle, label: "Failed" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const PAGE_SIZE = 10;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", PAGE_SIZE);
    if (filter !== "all") params.set("status", filter);

    api.get(`/customer/recharges?${params.toString()}`)
      .then(r => {
        setRows(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  if (loading) return (
    <div className="pt-8 text-center text-slate-400">
      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading history…
    </div>
  );

  return (
    <div className="pt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg text-brand-nav">Recharge History</h2>
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 bg-white">
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
          No recharges yet. Start by recharging your DTH!
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(r => {
            const s = statusStyle[r.status] || statusStyle.pending;
            const Icon = s.icon;
            return (
              <div key={r.id} className={`bg-white rounded-xl border ${s.border} p-4 hover:shadow-sm transition-shadow`}>
                {/* Top row: Plan name + Status */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full ${s.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-5 h-5 ${s.text}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-brand-nav text-sm truncate">{r.product_name || "Recharge"}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{fmtDate(r.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-bold text-brand-nav text-lg">₹{Number(r.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.text} mt-0.5`}>
                      {s.label}
                    </span>
                  </div>
                </div>

                {/* Details row */}
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400 block">Transaction ID</span>
                    <span className="font-mono font-semibold text-slate-600">{r.txn_no || "—"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">DTH ID</span>
                    <span className="font-mono font-semibold text-slate-600">{r.dth_id || "—"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Operator</span>
                    <span className="font-semibold text-slate-600 flex items-center gap-1">
                      <Tv className="w-3 h-3 text-slate-400" /> {r.operator || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Validity</span>
                    <span className="font-semibold text-slate-600">
                      {r.validity_days ? `${r.validity_days} days` : "—"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-slate-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition">
                  Previous
                </button>
                <span className="text-xs text-slate-500 font-medium">{page} / {pageCount}</span>
                <button disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
